import { useReactiveVar } from '@apollo/client'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  styled,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { useEffect, useState } from 'react'
import {
  BucketMediaContentOwner,
  BucketMediaFilter,
  BucketMediaSorter,
  BucketMediaSourceFilter,
  BucketMediaTypeFilter,
  BucketSortingField,
  BucketSortingOrder,
  BucketType
} from '../../../../graphql/generated'
import { modalVar } from '../../../../providers/apollo/cache'
import useBucketGallerySettings from '../../hooks/useBucketGallerySettings'
import useBucket from '../../hooks/useBucket'
import Crown from '../../../../svg/Crown'
import UpgradeButton from '../UpgradeButton'
import useProfilePlan from '../../hooks/useProfilePlan'

const SortRow = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center'
}))

const UpgradeBanner = styled(Box)(({ theme }) => ({
  width: '218px',
  background: theme.palette.groupsy.gradient,
  borderRadius: '8px',
  padding: theme.spacing(2),
  textAlign: 'center'
}))

// Helper function to map settings to sortValue string
const mapSettingsToSortValue = (sortBy: BucketMediaSorter | null | undefined): string => {
  if (!sortBy) return 'dateTakenDesc' // Default if no sort settings exist
  const { field, order } = sortBy
  if (field === BucketSortingField.DateTaken) {
    return order === BucketSortingOrder.Desc ? 'dateTakenDesc' : 'dateTakenAsc'
  }
  if (field === BucketSortingField.Date) {
    return order === BucketSortingOrder.Desc ? 'dateUploadedDesc' : 'dateUploadedAsc'
  }
  if (field === BucketSortingField.Title) {
    // return 'titleAsc' // Assuming Title is always Asc for now, adjust if needed
    return order === BucketSortingOrder.Desc ? 'titleDesc' : 'titleAsc'
  }
  return 'dateTakenDesc' // Default fallback
}

// Helper function to map sortValue string to settings
const mapSortValueToSettings = (sortValue: string): BucketMediaSorter => {
  switch (sortValue) {
    case 'dateTakenAsc':
      return { field: BucketSortingField.DateTaken, order: BucketSortingOrder.Asc }
    case 'dateUploadedDesc':
      return { field: BucketSortingField.Date, order: BucketSortingOrder.Desc }
    case 'dateUploadedAsc':
      return { field: BucketSortingField.Date, order: BucketSortingOrder.Asc }
    case 'titleAsc':
      return { field: BucketSortingField.Title, order: BucketSortingOrder.Asc }
    case 'titleDesc':
      return { field: BucketSortingField.Title, order: BucketSortingOrder.Desc }
    case 'dateTakenDesc':
    default:
      return { field: BucketSortingField.DateTaken, order: BucketSortingOrder.Desc }
  }
}

export default function FilterAndSort() {
  const modal = useReactiveVar(modalVar)
  const { settings, updateSettings } = useBucketGallerySettings()
  const { bucket, isFreemiumBucket } = useBucket()
  const { isFreemiumUser } = useProfilePlan()
  const bucketType = bucket?.bucketType
  const theme = useTheme()

  // Initial state from settings
  const [sortValue, setSortValue] = useState(() => mapSettingsToSortValue(settings.sortBy))
  const [filterByMediaType, setfilterByMediaType] = useState(() => {
    if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Image) return 'photos'
    if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Video) return 'videos'
    return 'all'
  })
  const [isFavorite, setIsFavorite] = useState(!!settings.filterBy?.isFavorite)
  const [filterByUploader, setFilterByUploader] = useState(() => {
    if (settings.filterBy?.owner === BucketMediaContentOwner.Others) return 'others'
    if (settings.filterBy?.owner === BucketMediaContentOwner.Me) return 'me'
    return 'all'
  })
  const [isMobile, setIsMobile] = useState(settings.filterBy?.source === BucketMediaSourceFilter.Mobile)

  useEffect(() => {
    // Update local state if gallery settings change externally
    setSortValue(mapSettingsToSortValue(settings.sortBy))
    setfilterByMediaType(() => {
      if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Image) return 'photos'
      if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Video) return 'videos'
      return 'all'
    })
    setIsFavorite(!!settings.filterBy?.isFavorite)
    setFilterByUploader(() => {
      if (settings.filterBy?.owner === BucketMediaContentOwner.Others) return 'others'
      if (settings.filterBy?.owner === BucketMediaContentOwner.Me) return 'me'
      return 'all'
    })
    setIsMobile(settings.filterBy?.source === BucketMediaSourceFilter.Mobile)
  }, [settings.sortBy, settings.filterBy])

  const handleClose = () => {
    modalVar(null)
    // Reset local state to match global settings if the modal is cancelled
    setSortValue(mapSettingsToSortValue(settings.sortBy))
    setfilterByMediaType(() => {
      if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Image) return 'photos'
      if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Video) return 'videos'
      return 'all'
    })
    setIsFavorite(!!settings.filterBy?.isFavorite)
    setFilterByUploader(() => {
      if (settings.filterBy?.owner === BucketMediaContentOwner.Others) return 'others'
      if (settings.filterBy?.owner === BucketMediaContentOwner.Me) return 'me'
      return 'all'
    })
    setIsMobile(settings.filterBy?.source === BucketMediaSourceFilter.Mobile)
  }

  const handleClearAll = () => {
    setSortValue('dateTakenDesc')
    setfilterByMediaType('all')
    setIsFavorite(false)
    setFilterByUploader('all')
    setIsMobile(false)
  }

  const handleClick = () => {
    const newSortSettings = mapSortValueToSettings(sortValue)
    const newFilterSettings: BucketMediaFilter = {}

    // 'all' means mediaType is null/undefined (no filter)
    newFilterSettings.mediaType = null
    if (filterByMediaType === 'photos') {
      newFilterSettings.mediaType = BucketMediaTypeFilter.Image
    } else if (filterByMediaType === 'videos') {
      newFilterSettings.mediaType = BucketMediaTypeFilter.Video
    }

    newFilterSettings.isFavorite = isFavorite

    newFilterSettings.owner = null
    if (bucketType !== BucketType.Mybucket) {
      if (filterByUploader === 'others') {
        newFilterSettings.owner = BucketMediaContentOwner.Others
      } else if (filterByUploader === 'me') {
        newFilterSettings.owner = BucketMediaContentOwner.Me
      }
    }

    newFilterSettings.source = isMobile ? BucketMediaSourceFilter.Mobile : null

    // Clean up empty filter object if no filters are applied
    const filterSettingsToSend = Object.keys(newFilterSettings).length > 0 ? newFilterSettings : null

    updateSettings({ sortBy: newSortSettings, filterBy: filterSettingsToSend }, { suppressAlerts: true })
    handleClose()
  }

  const isMWeb = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <div>
      <Dialog open={modal === 'filterAndSort'} onClose={handleClose} aria-labelledby="filter-sort-dialog-title" maxWidth="sm" fullWidth>
        <DialogTitle id="filter-sort-dialog-title">Filter and Sort</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl component="fieldset" disabled={isFreemiumBucket}>
              <FormLabel component="legend" sx={{ fontWeight: 'bold', color: '#000000', mb: 1, '&.Mui-focused': { color: '#2A2A2A' } }}>
                Sort By
              </FormLabel>
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <RadioGroup
                  aria-label="sort-by"
                  name="sort-by-group"
                  value={isFreemiumBucket ? 'dateUploadedDesc' : sortValue}
                  onChange={(e) => setSortValue(e.target.value)}
                >
                  <SortRow>
                    <FormControlLabel
                      value="dateUploadedDesc"
                      control={<Radio />}
                      label="Date Uploaded (Newest to Oldest)"
                      sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                    />
                  </SortRow>
                  <SortRow>
                    <FormControlLabel
                      value="dateUploadedAsc"
                      control={<Radio />}
                      label="Date Uploaded (Oldest to Newest)"
                      sx={{ mr: '4px', my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                    />
                    {isFreemiumBucket && <Crown sx={{ display: 'flex' }} size={20} color={theme.palette.groupsyPink[600]} />}
                  </SortRow>
                  <SortRow>
                    <FormControlLabel
                      value="dateTakenDesc"
                      control={<Radio />}
                      label="Date Taken (Newest to Oldest)"
                      sx={{ mr: '4px', my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                    />
                    {isFreemiumBucket && <Crown sx={{ display: 'flex' }} size={20} color={theme.palette.groupsyPink[600]} />}
                  </SortRow>
                  <SortRow>
                    <FormControlLabel
                      value="dateTakenAsc"
                      control={<Radio />}
                      label="Date Taken (Oldest to Newest)"
                      sx={{ mr: '4px', my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                    />
                    {isFreemiumBucket && <Crown sx={{ display: 'flex' }} size={20} color={theme.palette.groupsyPink[600]} />}
                  </SortRow>
                  <SortRow>
                    <FormControlLabel
                      value="titleAsc"
                      control={<Radio />}
                      label="Title (A to Z)"
                      sx={{ mr: '4px', my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                    />
                    {isFreemiumBucket && <Crown sx={{ display: 'flex' }} size={20} color={theme.palette.groupsyPink[600]} />}
                  </SortRow>
                  <SortRow>
                    <FormControlLabel
                      value="titleDesc"
                      control={<Radio />}
                      label="Title (Z to A)"
                      sx={{ mr: '4px', my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                    />
                    {isFreemiumBucket && <Crown sx={{ display: 'flex' }} size={20} color={theme.palette.groupsyPink[600]} />}
                  </SortRow>
                </RadioGroup>
                {isFreemiumBucket && isFreemiumUser && !isMWeb && (
                  <UpgradeBanner>
                    <Typography variant="pb.label.small" color="#FFFFFF" marginBottom={3}>
                      Become a premium user for advanced sorting options.
                    </Typography>
                    <UpgradeButton />
                  </UpgradeBanner>
                )}
              </Box>
              {isFreemiumBucket && isFreemiumUser && isMWeb && (
                <Box textAlign="center" marginTop={2}>
                  <Typography variant="pb.label.small" color="#727C8C" marginBottom={2}>
                    Become a premium user for advanced sorting options.
                  </Typography>
                  <UpgradeButton />
                </Box>
              )}
              {isFreemiumBucket && !isFreemiumUser && (
                <Box textAlign="center" marginTop={2} marginBottom={2}>
                  <Typography variant="pb.label.small" color="#727C8C">
                    Advanced sorting options are a premium feature.
                  </Typography>
                  <Typography variant="pb.label.small" color="#727C8C">
                    Contact the owner of this bucket to upgrade their plan.
                  </Typography>
                </Box>
              )}
            </FormControl>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 'bold', color: '#000000', mb: 1, '&.Mui-focused': { color: '#2A2A2A' } }}>
                Filter By Media Type
              </FormLabel>
              <RadioGroup
                aria-label="filter-by-media-type"
                name="filter-by-media-type-group"
                value={filterByMediaType}
                onChange={(e) => setfilterByMediaType(e.target.value)}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label="All"
                  sx={{ mr: '4px', my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
                <FormControlLabel
                  value="photos"
                  control={<Radio />}
                  label="Photos"
                  sx={{ mr: '4px', my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
                <FormControlLabel
                  value="videos"
                  control={<Radio />}
                  label="Videos"
                  sx={{ mr: '4px', my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
              </RadioGroup>
            </FormControl>
            {bucketType !== BucketType.Mybucket && (
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold', color: '#000000', mb: 1, '&.Mui-focused': { color: '#2A2A2A' } }}>
                  Filter By Uploader
                </FormLabel>
                <RadioGroup
                  aria-label="filter-by-uploader"
                  name="filter-by-uploader-group"
                  value={filterByUploader}
                  onChange={(e) => setFilterByUploader(e.target.value)}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label="All"
                    sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                  />
                  <FormControlLabel
                    value="me"
                    control={<Radio />}
                    label="Uploaded by Me"
                    sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                  />
                  <FormControlLabel
                    value="others"
                    control={<Radio />}
                    label="Uploaded by Others"
                    sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                  />
                </RadioGroup>
              </FormControl>
            )}
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 'bold', color: '#000000', mb: 1, '&.Mui-focused': { color: '#000000' } }}>
                Other Filters
              </FormLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FormControlLabel
                  control={<Checkbox checked={isFavorite} onChange={(e) => setIsFavorite(e.target.checked)} name="favorites" />}
                  label="Favorites"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
                <FormControlLabel
                  control={<Checkbox checked={isMobile} onChange={(e) => setIsMobile(e.target.checked)} name="mobileUploads" />}
                  label="Mobile Uploads"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
              </Box>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            py: 1.2,
            px: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 },
            position: 'relative'
          }}
        >
          <Button
            color="primary"
            onClick={handleClearAll}
            variant="text"
            sx={{
              mr: { xs: 0, sm: 'auto' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              fontWeight: 500,
              width: { xs: 'auto', sm: 'auto' },
              position: { xs: 'absolute', sm: 'static' },
              top: { xs: 0, sm: 'auto' },
              right: { xs: 16, sm: 'auto' }
            }}
          >
            Clear All Filters
          </Button>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              width: { xs: '100%', sm: 'auto' },
              mt: { xs: 3, sm: 0 }
            }}
          >
            <Button
              color="inherit"
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderRadius: '50px',
                padding: { xs: '6px 30px', sm: '8px 60px' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                flex: { xs: 1, sm: 'none' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleClick}
              variant="contained"
              sx={{
                borderRadius: '50px',
                padding: { xs: '6px 30px', sm: '8px 60px' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                flex: { xs: 1, sm: 'none' }
              }}
            >
              Apply
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </div>
  )
}
